import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams } from 'react-router-dom';
import { logger } from '@equip/utils/logger';

const ERROR_MESSAGES: Record<string, string> = {
  token_exchange_failed: 'Authentication token exchange failed. Please try signing in again.',
  no_code: 'No authorization code received. Please try signing in again.',
  invalid_state: 'Invalid authentication state. Please try signing in again.',
  invalid_token: 'Invalid authentication token. Please try signing in again.',
  session_expired: 'Your session has expired. Please sign in again.',
  unauthorized:
    'Your account is not authorized to access eQuip. Please contact your administrator to request access.',
  access_denied:
    'Access was denied. Please contact your administrator if you believe this is an error.',
  server_error: 'The identity provider returned an error. Please contact your administrator.',
  temporarily_unavailable:
    'The identity provider is temporarily unavailable. Please try again shortly.',
  unauthorized_client:
    'This application is not authorized for your account type. Please contact your administrator.',
  default: 'An authentication error occurred. Please try signing in again.',
};

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [loginError, setLoginError] = useState<string | null>(null);

  const errorMessage = useMemo(() => {
    const errorCode = searchParams.get('error');
    if (!errorCode) return null;
    const errorDescription = searchParams.get('error_description');
    const baseMessage = ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES.default;
    if (import.meta.env.DEV && errorDescription) {
      return `${baseMessage} (${errorCode}: ${errorDescription})`;
    }
    return baseMessage;
  }, [searchParams]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleLogin = async () => {
    setLoginError(null);
    try {
      await login();
    } catch (error) {
      logger.error('Login failed', { component: 'LoginPage', error: String(error) });
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      setLoginError(message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              eQuip
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Asset Management System
            </Typography>
          </Box>

          {(errorMessage ?? loginError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage ?? loginError}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<LoginIcon />}
            onClick={() => {
              void handleLogin();
            }}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            {isLoading ? 'Signing in\u2026' : 'Sign In'}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Use your organization credentials to access the system
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
